import React, { useEffect, useReducer, useRef, useState } from 'react'
import { HeadingPages } from '../../../../components/HeadingPages'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Box, Button, Center, Flex, Image as ChakraImage, Spinner, Text, useToast, useDisclosure } from '@chakra-ui/react'
import { api } from '../../../../services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { ArticleSectionsList } from './ArticleSectionsList'
import { Button as InnovationButton } from '../../../../components/Button'
import { FaArrowLeft, FaUpload, FaSearch } from 'react-icons/fa'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { ModalConfirmarRecriarConteudo } from './ModalConfirmarRecriarConteudo'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { ImFloppyDisk } from 'react-icons/im'
import { ModalPublicarArtigo } from './ModalPublicarArtigo'
import { ModalGerarNovasImagens } from './ModalGerarNovasImagens'
import { ModalBuscarImagensProduto } from './ModalBuscarImagensProduto'
import { user } from '../../../../helpers/user'
import { TextEditor } from '../../../../components/TextEditor'

const schemaArtigo = z.object({
  titulo: z.string(),
  hero_image: z.string(),
  alt_image: z.string(),
  slug: z.string(),
  paragrafo: z.string(),
  conclusao: z.object({
    paragrafo: z.string(),
    image_url: z.string(),
    alt_image: z.string(),
  }),
  secoes: z.array(
    z.object({
      titulo: z.string(),
      paragrafo: z.string(),
      id: z.number(),
    })
  ),
  id: z.string(),
})

const initialState = {
  editarParagrafoPrincipal: false,
  editarParagrafoConclusao: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'EDITAR_PARAGRAFO_PRINCIPAL':
      return { ...state, editarParagrafoPrincipal: !state.editarParagrafoPrincipal, editarParagrafoConclusao: false }
    case 'EDITAR_PARAGRAFO_CONCLUSAO':
      return { ...state, editarParagrafoConclusao: !state.editarParagrafoConclusao, editarParagrafoPrincipal: false }
    case 'EDITAR_HERO_IMAGE_ALT':
      return {
        ...state,
        editarHeroImageAlt: !state.editarHeroImageAlt,
        editarParagrafoPrincipal: false,
        editarParagrafoConclusao: false,
        editarConclusaoImageAlt: false,
      }
    case 'EDITAR_CONCLUSAO_IMAGE_ALT':
      return { ...state, editarConclusaoImageAlt: !state.editarConclusaoImageAlt, editarParagrafoPrincipal: false, editarParagrafoConclusao: false }

    default:
      return state
  }
}

async function recriarArtigo({ q, id }) {
  const response = await api.get(`/crm-interno/gerar-artigo`, {
    params: { q, id },
  })

  return response.data
}

async function publicarArtigo({ id, codigoUsuario }) {
  const response = await api.put(`/crm-interno/aprovar-artigo`, { artigo_id: id, aprovado: true, codigo_usuario: codigoUsuario })
  return response.data
}

async function uploadImageArticle({ id, imagem, artigo = false }) {
  const requiredWidth = 750
  const requiredHeight = 584

  const checkImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.src = URL.createObjectURL(file)

      img.onload = () => {
        const { width, height } = img
        if (width === requiredWidth && height === requiredHeight) {
          resolve(true)
        } else {
          reject(`A imagem precisa ter o tamanho ${requiredWidth}x${requiredHeight}. Tamanho atual: ${width}x${height}`)
        }
      }

      img.onerror = () => {
        reject('Erro ao carregar a imagem.')
      }
    })
  }

  try {
    await checkImageDimensions(imagem)

    const formData = new FormData()
    formData.append('imagem', imagem)
    formData.append('artigo', artigo)
    formData.append('id', id)

    const response = await api.post('/crm-interno/atualiza-imagem-artigo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response.data
  } catch (error) {
    throw new Error(error)
  }
}

async function updateArticle({ data }) {
  const response = await api.put(`/crm-interno/atualizar-artigo`, data)
  return response.data
}

async function getAllArticles({ slug }) {
  const response = await api.get(`/crm-interno/listar-artigo/${slug}`, {
    params: {
      aprovados: false,
    },
  })
  return response.data
}

async function gerarNovasImagens({ id }) {
  const response = await api.get(`/crm-interno/gerar-imagens-artigo`, {
    params: { id },
  })
  return response.data
}

export const Artigo = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const methods = useForm({
    resolver: zodResolver(schemaArtigo),
  })
  const toast = useToast({
    position: 'top-right',
    duration: 5000,
  })

  const queryClient = useQueryClient()

  const { control, handleSubmit, register, setValue, formState } = methods

  const [state, dispatch] = useReducer(reducer, initialState)

  const paragrafoConclusaoRef = useRef(null)
  const paragrafoPrincipalRef = useRef(null)
  const imageHeroRef = useRef(null)
  const imageConclusionRef = useRef(null)

  const { isOpen: isSearchImagesOpen, onOpen: onSearchImagesOpen, onClose: onSearchImagesClose } = useDisclosure()
  const [imageTarget, setImageTarget] = useState(null) // 'hero' ou 'conclusion'

  console.log(formState?.errors)

  const { data, isLoading, error } = useQuery({
    queryKey: ['articles', { slug }],
    queryFn: () => getAllArticles({ slug }),
    refetchOnWindowFocus: false,
    staleTime: 5000,
    onSuccess: (data) => {
      setValue('titulo', data.titulo)
      setValue('slug', data.slug)
      setValue('paragrafo', data.paragrafo)
      setValue('conclusao.paragrafo', data.conclusao.paragrafo)
      setValue('conclusao.image_url', data.conclusao.image_url)
      setValue('hero_image', data.hero_image)
      setValue('secoes', data.secoes)
      setValue('id', data.artigo_id)
    },
  })

  const { mutateAsync, isLoading: isLoadingUpdateArticle } = useMutation({
    mutationFn: updateArticle,
    onSuccess: () => {
      toast({
        title: 'Artigo salvo com sucesso!',
        status: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao salvar artigo',
        status: 'error',
      })
    },
  })

  const { mutateAsync: uploadImageArticleAsync } = useMutation({
    mutationFn: uploadImageArticle,
    onSuccess: (data) => {
      setValue('hero_image', data.urlImagem)

      queryClient.setQueryData(['articles', { slug }], (oldData) => {
        return { ...oldData, hero_image: data.urlImagem, alt_image: data.alt_image }
      })
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar imagem',
        description: error.message,
        status: 'error',
      })
    },
  })

  const { mutateAsync: uploadImageConclusionAsync } = useMutation({
    mutationFn: uploadImageArticle,
    onSuccess: (data) => {
      setValue('conclusao.image_url', data.urlImagem)

      queryClient.setQueryData(['articles', { slug }], (oldData) => {
        return { ...oldData, conclusao: { ...oldData.conclusao, image_url: data.urlImagem } }
      })
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar imagem',
        description: error.message,
        status: 'error',
      })
    },
  })

  const { mutateAsync: recriarArtigoAsync, isLoading: isLoadingRecriarArtigo } = useMutation({
    mutationFn: recriarArtigo,
    onSuccess: (data) => {
      queryClient.invalidateQueries('articles')
      navigate(`/adm/artigos/${data.slug}`)
    },
    onError: () => {
      toast({
        title: 'Erro ao recriar artigo',
        status: 'error',
      })
    },
  })

  const { mutateAsync: publicarArtigoAsync, isLoading: isLoadingPublicarArtigo } = useMutation({
    mutationFn: publicarArtigo,
    onSuccess: () => {
      queryClient.invalidateQueries('articles')
      navigate('/adm/artigos')

      toast({
        title: 'Artigo publicado com sucesso!',
        status: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao publicar artigo',
        status: 'error',
      })
    },
  })

  const { mutateAsync: gerarNovasImagensAsync, isLoading: isLoadingGerarNovasImagens } = useMutation({
    mutationFn: gerarNovasImagens,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['articles', { slug }])

      toast({
        title: 'Imagens geradas com sucesso!',
        status: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao gerar novas imagens',
        status: 'error',
      })
    },
  })

  async function handlePublicarArtigo() {
    await publicarArtigoAsync({ id: data.artigo_id, codigoUsuario: user.codigo_usuario })
  }

  async function handleUploadImageArticle(imagem, artigo) {
    await uploadImageArticleAsync({ id: data.artigo_id, imagem, artigo })
  }

  async function handleUploadImageConclusion(imagem, artigo) {
    await uploadImageConclusionAsync({ id: data.artigo_id, imagem, artigo })
  }

  async function handleUpdateArticle(data) {
    await mutateAsync({ data })
  }

  async function handleGerarNovasImagens() {
    await gerarNovasImagensAsync({ id: data.artigo_id })
  }

  const handleOpenSearchImages = (target) => {
    setImageTarget(target)
    onSearchImagesOpen()
  }

  const handleSelectProductImage = (image) => {
    if (!image || !image.LINK_IMAGEM) return

    const imageFile = {
      url: image.LINK_IMAGEM,
      alt: image.DESCRICAO_IMAGEM || '',
    }

    if (imageTarget === 'hero') {
      setValue('hero_image', imageFile.url)
      setValue('alt_image', imageFile.alt)

      queryClient.setQueryData(['articles', { slug }], (oldData) => {
        return { ...oldData, hero_image: imageFile.url, alt_image: imageFile.alt }
      })

      toast({
        title: 'Imagem principal atualizada com sucesso!',
        status: 'success',
      })
    } else if (imageTarget === 'conclusion') {
      setValue('conclusao.image_url', imageFile.url)
      setValue('conclusao.alt_image', imageFile.alt)

      queryClient.setQueryData(['articles', { slug }], (oldData) => {
        return {
          ...oldData,
          conclusao: {
            ...oldData.conclusao,
            image_url: imageFile.url,
            alt_image: imageFile.alt,
          },
        }
      })

      toast({
        title: 'Imagem de conclusão atualizada com sucesso!',
        status: 'success',
      })
    }
  }

  useEffect(() => {
    if (state.editarParagrafoPrincipal && paragrafoPrincipalRef.current) {
      paragrafoPrincipalRef.current.focus()
    }
    if (state.editarParagrafoConclusao && paragrafoConclusaoRef.current) {
      paragrafoConclusaoRef.current.focus()
    }

    if (state.editarHeroImageAlt && imageHeroRef.current) {
      imageHeroRef.current.focus()
    }

    if (state.editarConclusaoImageAlt && imageConclusionRef.current) {
      imageConclusionRef.current.focus()
    }
  }, [state.editarParagrafoPrincipal, state.editarParagrafoConclusao, state.editarHeroImageAlt, state.editarConclusaoImageAlt])

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    )
  }

  if (error) {
    return (
      <Box w="100%" mt="10px">
        <HeadingPages name="Erro" />
        <Flex justify="center" align="center" flexDir="column" gap={5}>
          <Box>
            <Box w="100%" p="20px" borderRadius="sm" bg="red.50">
              <Text fontSize="xl" fontWeight="bold" color="red.500" textAlign="center" textTransform="uppercase">
                Erro ao buscar artigo
              </Text>
            </Box>
          </Box>

          <Link to="/adm/artigos">
            <Text fontSize="lg" color="blue.500" fontWeight="bold">
              Voltar
            </Text>
          </Link>
        </Flex>
      </Box>
    )
  }

  if (!slug) navigate('/adm/artigos')

  return (
    <FormProvider {...methods}>
      <Box bgColor="white" as="form" onSubmit={handleSubmit(handleUpdateArticle)}>
        <HeadingPages name={`Artigo - ${data?.titulo}`} />

        <Flex flexDir="column" gap="20px" mt="20px" maxW="1280px" px="12px" mx="auto">
          <Flex alignItems="center" gap="25px">
            <Box fontSize="15px" border="1px solid #378BBF" p={1} maxW="1030px" w="full">
              https://innovationbrindes.com.br/responde/artigo/
              <Text as="span" color="#378BBF" fontWeight="bold" isTruncated>
                {data?.slug}
              </Text>
            </Box>

            <ModalConfirmarRecriarConteudo isLoading={isLoadingRecriarArtigo} onConfirm={() => recriarArtigoAsync({ q: slug, id: data.artigo_id })} />
          </Flex>
          <Flex flexDir="column" gap="25px" maxW="832px" w="full" mx="auto">
            <Box>
              <Text as="h1" fontSize="42px" fontWeight="bold">
                {data.titulo}
              </Text>
            </Box>
            <input type="hidden" {...register('id')} />

            <Box position="relative">
              <ChakraImage src={data.hero_image} alt={data.alt_image} w="full" h="auto" objectFit="cover" />
              <Flex direction="column" gap={2} position="absolute" right="-220px" top="0">
                <Button
                  as="label"
                  cursor="pointer"
                  htmlFor="hero_image"
                  borderColor="gray.700"
                  borderRadius="sm"
                  _hover={{ bg: 'blue.600' }}
                  _active={{ bg: 'blue.700' }}
                  variant="outline"
                  leftIcon={<FaUpload color="#378BBF" />}
                  onChange={(e) => handleUploadImageArticle(e.target.files[0], true)}
                >
                  <input id="hero_image" type="file" hidden />
                  Enviar nova imagem
                </Button>
                <Button
                  borderColor="gray.700"
                  borderRadius="sm"
                  _hover={{ bg: 'blue.600' }}
                  _active={{ bg: 'blue.700' }}
                  variant="outline"
                  leftIcon={<FaSearch color="#378BBF" />}
                  onClick={() => handleOpenSearchImages('hero')}
                >
                  Buscar imagens de produtos
                </Button>
              </Flex>
            </Box>
            <Box position="relative">
              <Controller
                name="alt_image"
                control={control}
                defaultValue={data.alt_image}
                render={({ field }) => (
                  <Box
                    contentEditable={state.editarHeroImageAlt}
                    fontSize="12px"
                    fontWeight="light"
                    dangerouslySetInnerHTML={{ __html: data.alt_image }}
                    onInput={(e) => field.onChange(e.target.innerHTML)}
                    {...field}
                    ref={imageHeroRef}
                  />
                )}
              />

              <Button
                position="absolute"
                borderColor="gray.700"
                right="-190px"
                borderRadius="sm"
                top="0"
                _hover={{ bg: 'blue.600' }}
                _active={{ bg: 'blue.700' }}
                variant="outline"
                leftIcon={<FaArrowLeft color="#378BBF" />}
                onClick={() => dispatch({ type: 'EDITAR_HERO_IMAGE_ALT' })}
              >
                Editar (alt)
              </Button>
            </Box>

            <Box position="relative">
              <Controller
                name="paragrafo"
                control={control}
                defaultValue={data.paragrafo}
                render={({ field }) => <TextEditor control={control} name="paragrafo" noImage {...field} />}
              />
            </Box>

            <ArticleSectionsList articles={data} />

            <Box position="relative" mt={15}>
              <Box borderTop="1px solid" borderColor="blue.500" paddingTop="10px" as="h2" fontSize="32px" fontWeight="bold">
                Conclusão
              </Box>
              <ChakraImage src={data.conclusao.image_url} alt={data.conclusao.alt_image} w="full" h="auto" objectFit="cover" />
              <Flex direction="column" gap={2} position="absolute" right="-220px" top="0">
                <Button
                  as="label"
                  cursor="pointer"
                  borderColor="gray.700"
                  borderRadius="sm"
                  _hover={{ bg: 'blue.600' }}
                  _active={{ bg: 'blue.700' }}
                  variant="outline"
                  leftIcon={<FaUpload color="#378BBF" />}
                  htmlFor="conclusao_image"
                  onChange={(e) => handleUploadImageConclusion(e.target.files[0], false)}
                >
                  <input id="conclusao_image" type="file" hidden />
                  Enviar nova imagem
                </Button>
                <Button
                  borderColor="gray.700"
                  borderRadius="sm"
                  _hover={{ bg: 'blue.600' }}
                  _active={{ bg: 'blue.700' }}
                  variant="outline"
                  leftIcon={<FaSearch color="#378BBF" />}
                  onClick={() => handleOpenSearchImages('conclusion')}
                >
                  Buscar imagens de produtos
                </Button>
              </Flex>
              <Box position="relative">
                <Controller
                  name="conclusao.alt_image"
                  control={control}
                  defaultValue={data.conclusao.alt_image}
                  render={({ field }) => (
                    <Box
                      contentEditable={state.editarConclusaoImageAlt}
                      fontSize="12px"
                      fontWeight="light"
                      dangerouslySetInnerHTML={{ __html: data.conclusao.alt_image }}
                      onInput={(e) => field.onChange(e.target.innerHTML)}
                      {...field}
                      ref={imageConclusionRef}
                    />
                  )}
                />

                <Button
                  position="absolute"
                  borderColor="gray.700"
                  right="-190px"
                  borderRadius="sm"
                  top="0"
                  _hover={{ bg: 'blue.600' }}
                  _active={{ bg: 'blue.700' }}
                  variant="outline"
                  leftIcon={<FaArrowLeft color="#378BBF" />}
                  onClick={() => dispatch({ type: 'EDITAR_CONCLUSAO_IMAGE_ALT' })}
                >
                  Editar (alt)
                </Button>
              </Box>
            </Box>

            <Box position="relative">
              <Controller
                name="conclusao.paragrafo"
                control={control}
                defaultValue={data.conclusao.paragrafo}
                render={({ field }) => <TextEditor control={control} name="conclusao.paragrafo" noImage {...field} />}
              />
            </Box>
          </Flex>
        </Flex>

        <Box
          as="footer"
          w="full"
          position="sticky"
          bottom="0"
          zIndex="sticky"
          borderTop="1px solid"
          borderColor="blue.500"
          p="10px"
          bg="white"
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDir="row"
          gap="20px"
        >
          <Flex alignItems="center" gap="2" as={Link} to="/adm/artigos">
            <FaArrowLeft color="#378BBF" />
            Voltar
          </Flex>

          <ModalGerarNovasImagens isLoading={isLoadingGerarNovasImagens} onConfirm={handleGerarNovasImagens} />

          <InnovationButton leftIcon={<ImFloppyDisk />} isLoading={isLoadingUpdateArticle} type="submit">
            Salvar alterações
          </InnovationButton>
          <ModalPublicarArtigo isLoading={isLoadingPublicarArtigo} onConfirm={handlePublicarArtigo} />
        </Box>
      </Box>

      <ModalBuscarImagensProduto
        isOpen={isSearchImagesOpen}
        onClose={onSearchImagesClose}
        onSelectImage={handleSelectProductImage}
        artigoId={data?.artigo_id}
      />
    </FormProvider>
  )
}
